import type { Member } from './types';

export const MEMBERS: Record<string, Member> = {
  'austin-dunbar': {
    id: 'austin-dunbar',
    name: 'Austin Dunbar',
    initials: 'AD',
    age: 38,
    location: 'Fort Thomas, KY',
    originallyFrom: 'Military family — grew up in Japan and Europe',
    family: 'Married, three daughters (19, 13, 12)',
    company: 'Durham Studio',
    otherVentures: 'Wenzel Distillery, solar/battery co., real estate holdings',
    industry: 'Design & Creative / Spirits',
    revenue: '$12–16M combined',
    revenueDetail: '$5M (Durham) + distillery + holdings',
    employees: '10–12 (studio)',
    stage: 'Stable, exploring 3-year exit',
    memberSince: 'Feb 9, 2026',
    nextRenewal: 'Feb 9, 2027',
    daysAsMember: 14,
    cohort: 'Needs mastermind placement',
    trafficLight: 'green',
    tagline: 'Design studio founder and world-class distiller exploring his first exit',
    personality: 'Direct, no-BS, lone wolf opening up for the first time. Hates fake networking. Values personality over portfolio.',
    callBrief: [
      'Brand new member (14 days). First community he has ever joined — treat this relationship carefully.',
      'Actively working on a 3-year exit strategy for Durham Studio. Raising EBITDA for 6–8x multiplier. Needs peers who have been through exits, not advisors.',
      'Wrestling with owner-to-operator separation. Looking for an operations director. "I need to be more owner than operator."',
      'His distillery just won World\'s Best Rye and World\'s Best Finished Whiskey. Great conversation starter.',
      'Anti-patterns: hates business card networking, fake enthusiasm, pitch events. Keep it real with him.',
    ],
    goals: [
      'Scale Durham beyond founder dependency',
      '3-year exit preparation',
      'Find operations director',
      'Publish a book (memoir + method)',
      'Diversify into passive investments',
    ],
    challenges: [
      'Key-man risk — clients want him, not his team',
      'Industry headwinds (creative going in-house)',
      'Never had peer support before',
      'Needs to build enterprise-level sales system',
    ],
    gives: [
      'Brand positioning & design systems',
      'Narrative clarity',
      '12+ years of pattern recognition from real builds',
      'Investment deal flow',
    ],
    needs: [
      'Exit strategy peers',
      'Ops director search guidance',
      'Service business scaling playbook',
      'Tax/saving strategies',
    ],
    emotionalTrajectory: 'Cautiously optimistic. Excited about FC but guarded — lone wolf instincts. Watch for withdrawal if early experiences don\'t feel authentic.',
    engagementVelocity: 'New — establishing baseline',
    churnRisk: 22,
    renewalReadiness: 68,
    engagementScore: 45,
    recentActivity: [
      { channel: 'Onboarding', detail: 'Completed app + Slack setup', when: 'Feb 12' },
      { channel: 'Slack', detail: 'Introduced himself in #general', when: 'Feb 14' },
      { channel: 'Call', detail: 'Scheduled first mastermind orientation', when: 'Feb 20' },
    ],
    suggestedActions: [
      { title: 'Introduce to Jeffrey Wazen', desc: 'Both wrestling with operator-to-owner transition. Jeffrey runs 40 locations, Austin runs a studio — different scale, same core challenge.', priority: 'high' },
      { title: 'Connect with Miami event attendees', desc: 'Austin travels to Fort Lauderdale/Miami frequently. Surface upcoming South Florida events.', priority: 'high' },
      { title: 'Follow up on mastermind placement', desc: 'Flagged as "SPECIAL CASE — NEEDS NEW MM." Ensure he gets placed in a group that feels authentic and high-caliber.', priority: 'high' },
    ],
    matches: [
      { id: 'jeffrey-wazen', reason: 'Both navigating owner-to-operator transition. Complementary industries, same age.' },
      { id: 'michael-jamieson', reason: 'Michael has 17 years of scaling operations experience. Austin needs that perspective for his exit prep.' },
      { id: 'steve-brudzewski', reason: 'Steve has built and exited before (Top Drawer Merch). Direct exit experience Austin is seeking.' },
    ],
  },

  'steve-brudzewski': {
    id: 'steve-brudzewski',
    name: 'Steve Brudzewski',
    initials: 'SB',
    age: 44,
    location: 'Miami, FL',
    originallyFrom: 'Toronto, via LA and Las Vegas',
    family: 'Not specified',
    company: 'Terrific (live commerce SaaS)',
    otherVentures: 'Feel Science (peptides/telehealth), advisor to 7 businesses',
    industry: 'SaaS / Live Commerce',
    revenue: '$5M (targeting $10M)',
    revenueDetail: 'Terrific SaaS + $350M GMV through LatAm',
    employees: 'Small core team, remote global',
    stage: 'Scaling — second full year',
    memberSince: 'Feb 16, 2026',
    nextRenewal: 'Feb 15, 2027',
    daysAsMember: 7,
    cohort: 'Not assigned yet',
    trafficLight: 'green',
    tagline: 'Serial founder, Forbes 30 Under 30 alum, self-described king of networking',
    personality: 'High-energy connector. Athlete mindset (D1 hockey). Generous giver. Moves fast, thinks big. Name-drops authentically.',
    callBrief: [
      'Newest member (7 days). Already completed onboarding. No mastermind survey submitted yet — needs follow-up.',
      'Wants to speak and contribute. Created his own networking format (Power Hour dinners). Let him lead something early.',
      'Just relocated to Miami from Las Vegas. Rebuilding his circle. Immediately ask about local connections needed.',
      'Has Carlos Slim as an investor for Terrific. Launching Feel Science (peptides) — planned for Jan 2026.',
      'Previous exit: Top Drawer Merch ($40–50M revenue, ~100 employees) acquired by Empire. Has real exit experience to share.',
    ],
    goals: [
      'Scale Terrific to $10M',
      'Launch Feel Science',
      'Build Miami founder circle',
      'Speaking opportunities',
      'Contribute and give back',
    ],
    challenges: [
      'New to Miami — needs local network',
      'Balancing multiple ventures',
      'Terrific still early in US market',
      'No mastermind group assigned yet',
    ],
    gives: [
      'Networking & business development',
      'Scaling from 1 to 100 employees',
      'Capital raise experience',
      'Strategic partnerships',
      'Exit experience (Top Drawer Merch)',
    ],
    needs: [
      'Curated Miami peer network',
      'Speaking platform',
      'Biohacking/health connections',
      'SaaS scaling peers',
    ],
    emotionalTrajectory: 'Enthusiastic and ready to engage. High energy from day one. Risk is under-engagement if not given a platform to contribute.',
    engagementVelocity: 'New — early signals positive',
    churnRisk: 15,
    renewalReadiness: 74,
    engagementScore: 52,
    recentActivity: [
      { channel: 'Onboarding', detail: 'Completed app + Slack setup', when: 'Feb 17' },
      { channel: 'Slack', detail: 'Posted intro and engaged with 3 members', when: 'Feb 18' },
      { channel: 'Slack', detail: 'Asked about Miami meetups in #events', when: 'Feb 21' },
    ],
    suggestedActions: [
      { title: 'Follow up on mastermind survey', desc: 'Only member of the four who hasn\'t submitted. Critical for placement.', priority: 'high' },
      { title: 'Invite to Miami members dinner', desc: 'He\'s already asking about local events. Connect him with other Miami-based members.', priority: 'high' },
      { title: 'Explore speaking opportunity', desc: 'His #1 ask is to speak and contribute. Consider for next virtual event or podcast.', priority: 'medium' },
    ],
    matches: [
      { id: 'austin-dunbar', reason: 'Steve has exit experience Austin needs. Austin has brand/creative depth Steve could use for Terrific.' },
      { id: 'jeffrey-wazen', reason: 'Both building multi-location/multi-product businesses. Jeffrey in telecom, Steve in SaaS — parallel scaling challenges.' },
      { id: 'michael-jamieson', reason: 'Michael wants AI conversations, Steve is launching a biotech play. Different domains, shared innovation mindset.' },
    ],
  },

  'jeffrey-wazen': {
    id: 'jeffrey-wazen',
    name: 'Jeffrey Wazen',
    initials: 'JW',
    age: 38,
    location: 'Montreal, QC (relocating to Dallas)',
    originallyFrom: 'Montreal',
    family: 'Not specified',
    company: 'Helium Wireless',
    otherVentures: 'None mentioned',
    industry: 'Telecom Retail',
    revenue: '$15M declining to $12M',
    revenueDetail: '40 locations: 26 branded + 14 sub-dealers',
    employees: '~101',
    stage: 'Stable but revenue declining',
    memberSince: 'Feb 12, 2026',
    nextRenewal: 'Feb 12, 2027',
    daysAsMember: 11,
    cohort: 'Digital Mastermind — 3rd Thursday 1-3pm EST',
    trafficLight: 'green',
    tagline: 'Telecom operator across 40 locations, fighting to stop being the bottleneck',
    personality: 'Super excited, loves to share, generous. Motivated by proving he can succeed beyond telecom. Wants friends who are founders, not just colleagues.',
    callBrief: [
      'Relocating from Montreal to Dallas — his #1 ask is to meet founders in his new city. Surface Dallas-based members immediately.',
      'Revenue declining ($15M to $12M). Wants to systematize operations so the business runs on metrics and leaders, not his daily intervention.',
      'Interested in franchising opportunities beyond telecom. Wants to prove he can be successful in something other than wireless.',
      'Connected to Mid-Day Squares founder. Wife interested in branding/marketing.',
      'No prior community memberships. Found FC through cold email outreach — high intent.',
    ],
    goals: [
      'Systematize 40-location operations',
      'Build leadership team that acts like owners',
      'Relocate to Dallas successfully',
      'Explore new business beyond telecom',
      'Find founder friends in new city',
    ],
    challenges: [
      'Revenue declining — needs to reverse trend',
      'Still the daily bottleneck across all locations',
      'Relocating without a local network',
      'Commission tracking and financial reporting unreliable',
      'No friends who are founders',
    ],
    gives: [
      'Multi-unit retail operations expertise',
      'Commission structure and team incentive design',
      'Frontline leadership development',
      'Scaling from 1 to 40 locations',
    ],
    needs: [
      'Operational systems that run without founder',
      'Dallas founder connections',
      'Leadership development frameworks',
      'Franchising mentorship',
    ],
    emotionalTrajectory: 'Excited but fragile. New member, new city, declining revenue. This is a critical window — early positive experiences will determine retention.',
    engagementVelocity: 'Moderate — enrolled in mastermind, completed onboarding',
    churnRisk: 38,
    renewalReadiness: 55,
    engagementScore: 48,
    recentActivity: [
      { channel: 'Onboarding', detail: 'Completed all onboarding steps', when: 'Feb 13' },
      { channel: 'Survey', detail: 'Submitted mastermind placement survey', when: 'Feb 17' },
      { channel: 'Mastermind', detail: 'Enrolled in digital cohort (3rd Thursday)', when: 'Feb 19' },
    ],
    suggestedActions: [
      { title: 'Introduce to Dallas-based members', desc: 'His #1 ask. This is make-or-break for his sense of belonging. Identify 2-3 Dallas founders for intros.', priority: 'high' },
      { title: 'Connect with Austin Dunbar', desc: 'Both wrestling with owner-to-operator transition. Different industries, same core challenge. High complementary value.', priority: 'high' },
      { title: 'Check in on revenue decline', desc: 'Revenue dropped from $15M to $12M. A thoughtful check-in could surface how FC can help — and prevent him from feeling alone in the decline.', priority: 'medium' },
    ],
    matches: [
      { id: 'austin-dunbar', reason: 'Both navigating owner-to-operator transition. Same age. Different industries keeps it fresh.' },
      { id: 'steve-brudzewski', reason: 'Steve has scaled businesses to 100+ employees and built operational systems. Jeffrey needs that exact playbook.' },
      { id: 'michael-jamieson', reason: 'Michael runs 560 employees across multiple cities — the scale Jeffrey aspires to, with operational wisdom to share.' },
    ],
  },

  'michael-jamieson': {
    id: 'michael-jamieson',
    name: 'Michael Jamieson',
    initials: 'MJ',
    age: 65,
    location: 'Lake Oswego, OR',
    originallyFrom: 'Southern Oregon',
    family: 'Married, sons (quarterly events planned)',
    company: 'Visiting Angels (Cassiel, Inc.)',
    otherVentures: 'Real estate holdings, adult care home (50/50 partnership)',
    industry: 'Healthcare / Home Care',
    revenue: '$25M',
    revenueDetail: '560 employees, 3 city markets (Portland, Eugene, Medford)',
    employees: '560',
    stage: 'Mature, 17-year franchise',
    memberSince: 'Feb 11, 2026',
    nextRenewal: 'Feb 11, 2027',
    daysAsMember: 12,
    cohort: 'Digital Mastermind — 3rd Thursday 1-3pm EST',
    trafficLight: 'green',
    tagline: 'Values-driven home care franchise builder, 560 employees, integrating AI',
    personality: 'Faith-based, compassionate, intellectually curious. Musician (piano, keyboards). Wants interesting conversations with diverse founders. Both teacher and learner.',
    callBrief: [
      'Most experienced member in this cohort — 65 years old, $25M revenue, 560 employees, 17 years in business. Respect the depth.',
      'Core value: "We choose compassion over profits." Practicing Christian, former music minister. Faith and business are integrated for him.',
      'Actively integrating AI into home care operations — caregiver hiring already AI-powered, launching client-facing AI next.',
      'His #1 ask is to connect with AI founders. He wants to learn from people building in that space.',
      'NDA was never sent — this is a process gap that needs fixing. He is the only member without one.',
    ],
    goals: [
      'Fully integrate AI into case management',
      'Improve profit margins',
      'Hit 13,000 billable hours target',
      'Real estate development (plat lot, sell Macadam)',
      'Quarterly events with sons',
    ],
    challenges: [
      'Finding enough caregivers (demand outpacing supply)',
      'Reaching primary referral sources',
      'Regulatory environment',
      'Existing peer groups are stale',
      'Being the oldest in most rooms',
    ],
    gives: [
      'Communication & conflict resolution',
      'Process/project implementation',
      'Budgeting management',
      '17 years of franchise scaling wisdom',
      'Values-driven leadership perspective',
    ],
    needs: [
      'AI founder connections',
      'Fresh perspectives from diverse industries',
      'Real estate development guidance',
      'Intellectually stimulating conversations',
    ],
    emotionalTrajectory: 'Thoughtful and measured. Rated himself 8/10 on interest — wanted to pray on it before joining. Engaged but deliberate.',
    engagementVelocity: 'Moderate — enrolled, onboarding complete, survey submitted',
    churnRisk: 28,
    renewalReadiness: 62,
    engagementScore: 44,
    recentActivity: [
      { channel: 'Onboarding', detail: 'Completed all setup steps', when: 'Feb 12' },
      { channel: 'Survey', detail: 'Submitted mastermind placement survey', when: 'Feb 18' },
      { channel: 'Mastermind', detail: 'Enrolled in digital cohort (3rd Thursday)', when: 'Feb 19' },
    ],
    suggestedActions: [
      { title: 'Send NDA', desc: 'Michael is the only sample member without a signed NDA. This is a process gap — resolve immediately.', priority: 'high' },
      { title: 'Introduce to AI-focused members', desc: 'His one ask from FC. Already using AI in operations — connect him with members building or implementing AI.', priority: 'high' },
      { title: 'Position as mentor figure', desc: 'At 65 with $25M and 560 employees, he has wisdom younger founders need. Consider for mentorship matching or speaking.', priority: 'medium' },
    ],
    matches: [
      { id: 'austin-dunbar', reason: 'Michael has decades of operational scaling. Austin is preparing for his first exit — Michael\'s perspective would be invaluable.' },
      { id: 'jeffrey-wazen', reason: 'Jeffrey runs 40 locations with 101 employees, declining revenue. Michael runs 560 employees profitably — direct mentor potential.' },
      { id: 'steve-brudzewski', reason: 'Both interested in health/biotech. Steve\'s Feel Science and Michael\'s home care are adjacent. Shared curiosity about innovation.' },
    ],
  },

  // Additional members for a fuller sidebar
  'sarah-chen': {
    id: 'sarah-chen',
    name: 'Sarah Chen',
    initials: 'SC',
    age: 41,
    location: 'Austin, TX',
    company: 'NovaBridge Analytics',
    industry: 'Data Analytics SaaS',
    revenue: '$8M',
    memberSince: 'Jan 5, 2026',
    trafficLight: 'green',
    daysAsMember: 49,
    churnRisk: 12,
    renewalReadiness: 82,
    engagementScore: 78,
    tagline: 'Data analytics SaaS founder, scaling B2B enterprise',
  },

  'derek-washington': {
    id: 'derek-washington',
    name: 'Derek Washington',
    initials: 'DW',
    age: 52,
    location: 'Atlanta, GA',
    company: 'Pinnacle Construction Group',
    industry: 'Construction / Real Estate',
    revenue: '$32M',
    memberSince: 'Nov 15, 2025',
    trafficLight: 'amber',
    daysAsMember: 100,
    churnRisk: 45,
    renewalReadiness: 48,
    engagementScore: 31,
    tagline: 'Commercial construction firm, going quiet after strong start',
  },

  'maria-santos': {
    id: 'maria-santos',
    name: 'Maria Santos',
    initials: 'MS',
    age: 36,
    location: 'Denver, CO',
    company: 'Verdant Skincare',
    industry: 'Beauty / CPG',
    revenue: '$4.2M',
    memberSince: 'Dec 1, 2025',
    trafficLight: 'green',
    daysAsMember: 84,
    churnRisk: 18,
    renewalReadiness: 76,
    engagementScore: 69,
    tagline: 'Clean beauty CPG founder, rapid DTC growth',
  },

  'raj-patel': {
    id: 'raj-patel',
    name: 'Raj Patel',
    initials: 'RP',
    age: 47,
    location: 'Chicago, IL',
    company: 'MedConnect Solutions',
    industry: 'Healthcare Tech',
    revenue: '$11M',
    memberSince: 'Sep 20, 2025',
    trafficLight: 'red',
    daysAsMember: 156,
    churnRisk: 72,
    renewalReadiness: 28,
    engagementScore: 14,
    tagline: 'Healthcare SaaS founder, disengaged for 2+ months',
  },

  'lisa-nakamura': {
    id: 'lisa-nakamura',
    name: 'Lisa Nakamura',
    initials: 'LN',
    age: 39,
    location: 'San Francisco, CA',
    company: 'Trellis Marketing',
    industry: 'Marketing Agency',
    revenue: '$6.5M',
    memberSince: 'Oct 8, 2025',
    trafficLight: 'green',
    daysAsMember: 138,
    churnRisk: 20,
    renewalReadiness: 71,
    engagementScore: 65,
    tagline: 'Performance marketing agency, strong Slack contributor',
  },

  'tom-fitzgerald': {
    id: 'tom-fitzgerald',
    name: 'Tom Fitzgerald',
    initials: 'TF',
    age: 58,
    location: 'Dallas, TX',
    company: 'Fitzgerald Hospitality',
    industry: 'Hotels / Hospitality',
    revenue: '$18M',
    memberSince: 'Aug 12, 2025',
    trafficLight: 'amber',
    daysAsMember: 195,
    churnRisk: 40,
    renewalReadiness: 52,
    engagementScore: 38,
    tagline: 'Boutique hotel group, renewable coming up',
  },
} as const satisfies Record<string, Member>;

export type MemberId = keyof typeof MEMBERS;
